import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/24x24/info.svg';
import {
  Flex,
  FlexColumn,
  InternalLink,
  PageAlert,
  SearchField,
} from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { WarehouseType } from '@elementary/common-ui/types';
import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import { AddTestForm, Resource, TestTemplateMetadata } from 'shared/types';
import {
  CLOUD_TESTS_UNSUPPORTED_WAREHOUSES,
  CLOUD_TESTS_PARTIAL_SUPPORT_WAREHOUSES,
} from 'shared/constants';
import { TestCatalogFilters } from './QualityDimensionTabs';
import TestCard from './TestCard';

const Content = styled(FlexColumn)`
  padding: 32px;
  gap: 32px;
  overflow: auto;
`;

const CardsWrapper = styled(Flex)`
  flex-wrap: wrap;
  gap: 32px;
`;

const VIEW_MATERIALIZATION = ['view', 'ephemeral', 'materialized_view'];
const PIPELINE_TEST_SUPPORTED_TYPES = ['model', 'seed', 'snapshot'];

export type CardTemplate = {
  key: string;
  testName: string;
  packageName: string;
  description: string;
  qualityDimension?: string;
  testType: AddTestForm['testType'];
  popularity?: number;
  comingSoon?: boolean;
};

type Props = {
  testCatalogFilters: TestCatalogFilters;
  selectedTemplateId?: string;
  onCardClick: (val: CardTemplate) => void;
  elementaryTests: TestTemplateMetadata[];
  testsTemplatesData?: TestTemplateMetadata[];
  shouldConnectCodeRepo?: boolean;
  selectedTableResourcesData: Resource[];
  warehouseType?: WarehouseType;
};

const TestCardsSection = ({
  testCatalogFilters,
  selectedTemplateId,
  onCardClick,
  elementaryTests,
  testsTemplatesData = [],
  shouldConnectCodeRepo,
  selectedTableResourcesData = [],
  warehouseType,
}: Props) => {
  const [search, setSearch] = useState('');

  const isSearchMatch = useCallback(
    (template: CardTemplate) =>
      search
        ? template.testName?.toLowerCase().includes(search.toLowerCase()) ||
          template.description?.toLowerCase().includes(search.toLowerCase())
        : true,
    [search],
  );

  const isQuickFilterMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.quickFilter) {
        case 'all':
          return true;
        case 'popular':
          return template.popularity === 1;
        default:
          return true;
      }
    },
    [testCatalogFilters.quickFilter],
  );

  const isSourceMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.sourceFilter) {
        case 'elementary cloud':
          return template.packageName === 'ELEMENTARY_CLOUD';
        case 'dbt':
          return template.packageName !== 'ELEMENTARY_CLOUD';
        default:
          return true;
      }
    },
    [testCatalogFilters.sourceFilter],
  );

  const isCategoryMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.categoryFilter) {
        case undefined:
          return true;
        case 'other':
          return !template.qualityDimension;
        default:
          return (
            template.qualityDimension === testCatalogFilters.categoryFilter
          );
      }
    },
    [testCatalogFilters.categoryFilter],
  );

  const mapTestTemplateToCard = (
    testTemplate: TestTemplateMetadata,
    testType?: string,
    popularity?: number,
  ): CardTemplate => {
    const [packageName, testName] = testTemplate.test_name.split('.');

    return {
      key: testTemplate.test_name,
      testName:
        packageName === 'elementary_cloud' ? `cloud_${testName}` : testName,
      packageName: packageName?.toUpperCase(),
      description: testTemplate.description,
      qualityDimension: testTemplate.quality_dimension,
      testType: (testType ||
        testName
          .split('_')
          .map((word, i) => (i > 0 ? capitalize(word) : word))
          .join('')) as AddTestForm['testType'],
      popularity,
      comingSoon: testTemplate.coming_soon,
    };
  };

  const getTestTemplatesTestType = (testTemplate: TestTemplateMetadata) => {
    const [packageName, testName] = testTemplate.test_name.split('.');

    if (packageName === 'elementary_cloud') {
      const capitalizedTestName = testName
        .split('_')
        .map((word) => capitalize(word))
        .join('') as AddTestForm['testType'];

      return `cloud${capitalizedTestName}`;
    }
    return 'yamlTest';
  };

  const testCardsData: CardTemplate[] | undefined = useMemo(
    () =>
      testsTemplatesData
        .map((testTemplate) =>
          mapTestTemplateToCard(
            testTemplate,
            getTestTemplatesTestType(testTemplate),
            testTemplate?.popularity,
          ),
        )
        .concat(
          elementaryTests.map((testTemplate) =>
            mapTestTemplateToCard(testTemplate, undefined, 1),
          ),
        )
        .filter(
          (testTemplate) =>
            isSearchMatch(testTemplate) &&
            isQuickFilterMatch(testTemplate) &&
            isSourceMatch(testTemplate) &&
            isCategoryMatch(testTemplate),
        ),
    [
      elementaryTests,
      isCategoryMatch,
      isQuickFilterMatch,
      isSearchMatch,
      isSourceMatch,
      testsTemplatesData,
    ],
  );

  const isTemplateSelected = (template: CardTemplate) => {
    const templateId =
      template.testType === 'yamlTest' ? template.key : template.testType;
    return !!templateId && templateId === selectedTemplateId;
  };

  const hasViewModelsResource = useMemo(
    () =>
      selectedTableResourcesData?.some(
        (resource) =>
          resource.materialization &&
          VIEW_MATERIALIZATION.includes(resource.materialization),
      ),
    [selectedTableResourcesData],
  );

  const unsupportedCloudTestsResources = useMemo(
    () =>
      selectedTableResourcesData?.filter(
        (resource) => resource.cloud_tests_supported === false,
      ) ?? [],
    [selectedTableResourcesData],
  );

  const getCloudTestDisabledReason = (
    testTemplate: CardTemplate,
  ): string | undefined => {
    if (testTemplate.packageName !== 'ELEMENTARY_CLOUD') {
      return undefined;
    }

    const warehouseDisplayName = warehouseType
      ? WAREHOUSE_DISPLAY_NAMES[warehouseType]
      : '';

    if (
      warehouseType &&
      CLOUD_TESTS_UNSUPPORTED_WAREHOUSES.includes(warehouseType)
    ) {
      return `Cloud tests aren't supported for ${warehouseDisplayName} yet.`;
    }

    if (
      warehouseType &&
      CLOUD_TESTS_PARTIAL_SUPPORT_WAREHOUSES.includes(warehouseType)
    ) {
      return `Cloud tests are only supported for Iceberg tables on ${warehouseDisplayName}.`;
    }

    if (testTemplate.testType === 'cloudPipelineTaskPerformance') {
      const unsupportedResource = selectedTableResourcesData.find(
        (resource) =>
          resource.table_type &&
          !PIPELINE_TEST_SUPPORTED_TYPES.includes(resource.table_type),
      );
      if (unsupportedResource) {
        const assetType = unsupportedResource.table_type ?? 'unknown';
        return `Pipeline task performance tests don't support ${assetType} assets. Deselect them to create this test.`;
      }
      return undefined;
    }

    if (
      hasViewModelsResource &&
      testTemplate.testType !== 'cloudDataContract'
    ) {
      return "Cloud tests don't support views. Deselect views to create this test.";
    }

    if (unsupportedCloudTestsResources.length === 0) {
      return undefined;
    }

    if (
      unsupportedCloudTestsResources.length ===
      selectedTableResourcesData.length
    ) {
      return 'Cloud tests cannot be created for tables not found in the data warehouse metadata';
    }

    if (unsupportedCloudTestsResources.length === 1) {
      const tableName =
        unsupportedCloudTestsResources[0].fqn ??
        unsupportedCloudTestsResources[0].name;
      return `Cloud tests cannot be created for ${tableName}. Remove it from your selection to continue.`;
    }

    return `Cloud tests cannot be created for ${unsupportedCloudTestsResources.length} of your selected tables because they are not found in the data warehouse metadata. Remove them from your selection to continue.`;
  };

  return (
    <FlexColumn sx={{ width: '100%' }}>
      {shouldConnectCodeRepo && (
        <PageAlert icon={<InfoIcon />}>
          Only cloud tests can be added.{' '}
          <InternalLink to="/settings/environments">
            Connect your code repository
          </InternalLink>{' '}
          to add all test types from the UI.
        </PageAlert>
      )}
      <Content>
        <SearchField
          sx={{ width: '248px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CardsWrapper>
          {testCardsData?.map((testTemplate) => {
            const disabledReason = testTemplate.comingSoon
              ? 'Coming soon'
              : getCloudTestDisabledReason(testTemplate);
            return (
              <TestCard
                key={testTemplate.key}
                testName={testTemplate.testName}
                packageName={testTemplate.packageName}
                description={testTemplate.description}
                selected={isTemplateSelected(testTemplate) && !disabledReason}
                onClick={() =>
                  disabledReason ? null : onCardClick(testTemplate)
                }
                disabledReason={disabledReason}
                comingSoon={testTemplate.comingSoon}
              />
            );
          })}
        </CardsWrapper>
      </Content>
    </FlexColumn>
  );
};

export default TestCardsSection;
